/**
 * Sets sensible defaults on MenuPage mount so users can browse immediately.
 * - orderType defaults to 'pickup'
 * - pickupInfo defaults to ASAP (when store is open)
 * - Handles ?location={slug} URL param for shareable links
 */

import { useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setOrderType, setPickupInfo, setSelectedLocationId } from '@/store/orderSlice'
import { useMenuData } from '@/hooks/useMenuData'
import { useSelectedLocation } from '@/hooks/useSelectedLocation'
import { getEffectiveSchedule } from '@/utils/effectiveSchedule'
import { getStoreStatus } from '@/utils/storeHours'
import { slugifyLocationName } from '@/utils/slugifyLocationName'

export function useMenuDefaults() {
  const dispatch = useAppDispatch()
  const order = useAppSelector((s) => s.order)
  const { restaurant } = useMenuData()
  const { location, menuLocations } = useSelectedLocation()

  // Default orderType to 'pickup' if not set
  useEffect(() => {
    if (!order.orderType) {
      dispatch(setOrderType('pickup'))
    }
  }, [order.orderType, dispatch])

  // Default pickupInfo to ASAP when store is open and no time selected
  useEffect(() => {
    if (order.pickupInfo) return
    if (!location && menuLocations.length !== 1) return

    const schedule = getEffectiveSchedule(location, restaurant)
    const status = getStoreStatus(schedule.hours, {
      hoursExceptions: schedule.hoursExceptions,
      businessTimeZone: schedule.timeZone,
    })

    if (status.isOpen) {
      dispatch(setPickupInfo({ pickupTime: 'ASAP', scheduledPickupAt: null }))
    }
  }, [order.pickupInfo, location, menuLocations.length, restaurant, dispatch])

  // Handle ?location={slug} URL param
  useEffect(() => {
    if (menuLocations.length === 0 || order.selectedLocationId) return

    const params = new URLSearchParams(window.location.search)
    const locationParam = params.get('location')
    if (!locationParam) return

    const match = menuLocations.find(
      (loc) => slugifyLocationName(loc.location_name) === locationParam
    )
    if (match) {
      dispatch(setSelectedLocationId(match.id))
    }
  }, [menuLocations, order.selectedLocationId, dispatch])
}
